"use client";

import { useEffect } from "react";
import { auth } from "@/config/firebaseApp";
import { setAuthServerToken } from "@/modules/users/service/auth";
import { getFirebaseTokenFromCookies } from "@/modules/users/service/cookies";
import { errorHandler } from "@/config/errorReporting";

/**
 * Mantiene fresco el token que está en la cookie.
 *
 * El token se guardaba una sola vez, al iniciar sesión. Los tokens de Firebase
 * duran una hora, así que a partir de ahí todo lo que lee la cookie empezaba a
 * fallar con 401: el render del servidor, y también los guardados del
 * navegador, que van con `getFirebaseTokenFromCookies()` y no con
 * `getIdToken()`.
 *
 * Las consecuencias no se veían como un error de sesión, y por eso costó
 * encontrarlo:
 *
 * - Un examen de salud que se guardaba sin el dibujo del reloj, devolviendo 200.
 * - Al supervisor operativo le reaparecía el botón de descargar el protocolo,
 *   porque sin poder leer el perfil los permisos quedaban abiertos.
 * - Pantallas que "tardaban" o mostraban un error genérico al volver de un
 *   rato de inactividad.
 *
 * El SDK de Firebase renueva el token solo, unos minutos antes de que venza, y
 * avisa por `onIdTokenChanged`. Acá lo único que se hace es escribir en la
 * cookie el token nuevo cada vez que eso pasa.
 */
export default function RefrescarTokenDeSesion() {
	useEffect(() => {
		return auth.onIdTokenChanged(async (usuario) => {
			if (!usuario) return;

			try {
				/**
				 * Sólo refresca una sesión abierta; nunca abre una cerrada.
				 *
				 * "Cerrar sesión" del portal borra las cookies pero no hace
				 * `signOut` de Firebase, así que la sesión del navegador sigue
				 * viva. Sin esta guarda, el primer refresco de token después
				 * de cerrar sesión volvía a escribir la cookie y la persona
				 * quedaba logueada de nuevo.
				 */
				if (!(await getFirebaseTokenFromCookies())) return;

				const { token, expirationTime } = await usuario.getIdTokenResult();
				await setAuthServerToken(token, usuario.uid, expirationTime);
			} catch (error) {
				// Sin datos personales: alcanza con saber que no se pudo renovar.
				errorHandler?.report(
					`No se pudo refrescar el token de la cookie: ${error}`,
				);
			}
		});
	}, []);

	return null;
}
